import { quantileFromSorted } from "@/utils/quantile"

// Tukey outlier detection for scatter points. Pure — no React or chart deps —
// so the plot and its tests can both call it.

interface TukeyFences {
  low: number
  high: number
}

// IQR multipliers: 1.5x is Tukey's inner fence (flags mild + extreme outliers),
// 3x the outer fence ("far out"; only egregious extremes). Absent config === 1.5.
export const DEFAULT_OUTLIER_IQR_MULTIPLIER = 1.5
export const CONSERVATIVE_OUTLIER_IQR_MULTIPLIER = 3

// Below this many finite values the IQR fences can't separate a point from the
// sample, so there is nothing meaningful to flag.
const MIN_SAMPLE_FOR_FENCES = 4

/**
 * Tukey k×IQR fences for a numeric sample (k defaults to the 1.5x inner fence).
 * Null when there are too few finite values for the fences to mean anything.
 */
export function tukeyFences(
  values: readonly number[],
  k: number = DEFAULT_OUTLIER_IQR_MULTIPLIER,
): TukeyFences | null {
  const finite = values.filter((v) => Number.isFinite(v))
  if (finite.length < MIN_SAMPLE_FOR_FENCES) {
    return null
  }
  const sorted = [...finite].sort((a, b) => a - b)
  const q1 = quantileFromSorted(sorted, 0.25)
  const q3 = quantileFromSorted(sorted, 0.75)
  const iqr = q3 - q1
  return { low: q1 - k * iqr, high: q3 + k * iqr }
}

// Point fields the scan needs — a structural subset of ScatterPoint.
export interface OutlierScanPoint {
  simulationOutputId: string
  x: number
  y: number
}

function isOutside(value: number, fences: TukeyFences | null): boolean {
  return fences !== null && (value < fences.low || value > fences.high)
}

/**
 * Ids of points outside the Tukey fences on at least one numeric axis (union of
 * X and Y). Categorical axes are skipped: their coordinates are ordinal category
 * indices, so a spread-based fence would be meaningless. `k` sets fence width
 * (default 1.5x IQR).
 */
export function findScatterOutliers(
  points: readonly OutlierScanPoint[],
  numericAxes: { x: boolean; y: boolean },
  k: number = DEFAULT_OUTLIER_IQR_MULTIPLIER,
): Set<string> {
  const xValues = numericAxes.x ? points.map((p) => p.x) : null
  const yValues = numericAxes.y ? points.map((p) => p.y) : null
  const xFences = xValues ? tukeyFences(xValues, k) : null
  const yFences = yValues ? tukeyFences(yValues, k) : null
  const ids = new Set<string>()
  if (!xFences && !yFences) {
    return ids
  }
  for (const point of points) {
    if (isOutside(point.x, xFences) || isOutside(point.y, yFences)) {
      ids.add(point.simulationOutputId)
    }
  }
  return ids
}
